import React, { useContext } from "react";
import styled from "styled-components";
import { GatsbyImage } from "gatsby-plugin-image";
import { GlobalDispatchContext, GlobalStateContext } from "../context";
import { CSSTransition } from "react-transition-group";
import { useStaticQuery, graphql } from "gatsby";
import { useIntl, navigate } from "gatsby-plugin-intl";
import get from "lodash/get";

import NavLink from "./navLink";
import MenuIcon from "../common/menuIcon";

const StyledWrapper = styled.div`
  flex: 1;
  flex-basis: 0;
  ${(props) => props.theme.responsive.desktop} {
    display: none;
  }
`;

const StyledNavButton = styled.div`
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  path {
    transition: fill 1s;
    fill: ${(props) =>
      props.isNavShown ? props.theme.color.white : props.theme.color.darkGrey};
  }

  background-color: ${(props) =>
    props.isNavShown ? props.theme.color.darkGrey : "transparent"};

  transition: background-color 1s;
  &:hover {
    cursor: pointer;
  }
`;

const StyledNav = styled.div`
  box-sizing: border-box;
  display: ${(props) => (props.isShown ? "block" : "none")};
  position: fixed;
  top: ${(props) => (props.isInstallBannerShown ? "8.6rem" : "4.6rem")};
  left: 0;
  right: 0;
  width: 100vw;
  bottom: 0;
  background-color: ${(props) => props.theme.color.darkGrey};
  overflow: scroll;
  padding: 2.6rem 0 7rem 0;
  font-family: ${(props) => props.theme.font.sans};

  opacity: 1;
  &.fade-enter {
    opacity: 0;
    display: block;
  }

  &.fade-enter-active {
    opacity: 1;
    transition: 1s opacity;
  }

  &.fade-enter-done {
    opacity: 1;
    display: block;
  }

  &.fade-exit {
    opacity: 1;
  }

  &.fade-exit-active {
    opacity: 0;
    transition: 0.3s opacity;
  }

  &.fade-exit-done {
    opacity: 0;
    display: none;
  }
`;

const ContactWrapper = styled.div`
  color: ${(props) => props.theme.color.grey};
  font-size: 0.7rem;
  margin: 3rem 1rem 0 1rem;
`;

const ContactEmail = styled.div`
  color: ${(props) => props.theme.color.white};
  font-weight: bold;
  font-size: 1rem;
`;

const NavLinkWrapper = styled.div`
  margin: 0 1rem;
`;

const ContactPhone = styled.div`
  color: ${(props) => props.theme.color.white};
  margin-bottom: 0.6rem;
`;

export default function MobileNavMenu({ color, ...props }) {
  const intl = useIntl();
  const state = useContext(GlobalStateContext);
  const dispatch = useContext(GlobalDispatchContext);
  const {
    strapi: {
      layout: { nav: nav },
    },
  } = useStaticQuery(graphql`
    {
      strapi {
        layout {
          nav {
            ... on STRAPI_ComponentBaseLink {
              __typename
              isDesktop
              link
              title {
                en
                es
                it
              }
            }
            ... on STRAPI_ComponentBaseDropdownLink {
              __typename
              isDesktop
              link
              sublinks {
                title {
                  en
                  es
                  it
                }
                link
              }
              title {
                en
                es
                it
              }
            }
          }
        }
      }
    }
  `);
  const { isMobileNavShown, isInstallBannerShown } = state;

  const handleLinkClick = (slug) => {
    navigate(`/${slug}`);
    dispatch({
      type: "SET_IS_MOBILE_NAV_SHOWN",
      isShown: false,
    });
  };

  return (
    <StyledWrapper>
      <StyledNavButton
        isNavShown={isMobileNavShown}
        onClick={() => {
          dispatch({
            type: "SET_IS_MOBILE_NAV_SHOWN",
            isShown: !isMobileNavShown,
          });
        }}
      >
        <MenuIcon color={color} isOpen={isMobileNavShown} />
      </StyledNavButton>
      <CSSTransition in={isMobileNavShown} timeout={1000} classNames="fade">
        <StyledNav
          className="fade"
          isInstallBannerShown={isInstallBannerShown}
          isShown={isMobileNavShown}
        >
          <NavLinkWrapper>
            {nav.map((navLink) => {
              if (navLink.__typename === "STRAPI_ComponentBaseDropdownLink") {
                return [
                  <NavLink
                    key={navLink.link}
                    title={get(navLink.title, intl.locale, "")}
                    handleClick={() => handleLinkClick(navLink.link)}
                  />,
                  ...navLink.sublinks.map((sublink) => (
                    <NavLink
                      key={sublink.link}
                      title={get(sublink.title, intl.locale, "")}
                      handleClick={() => handleLinkClick(sublink.link)}
                    />
                  )),
                ];
              } else if (navLink.__typename === "STRAPI_ComponentBaseLink") {
                return (
                  <NavLink
                    key={navLink.link}
                    title={get(navLink.title, intl.locale, "")}
                    handleClick={() => handleLinkClick(navLink.link)}
                  />
                );
              }
            })}
          </NavLinkWrapper>
          <ContactWrapper>
            <ContactEmail>customer.service@kingchildren.com</ContactEmail>
            <ContactPhone>+1 ‪(347) 759-6340‬</ContactPhone>

            <div>King Children‬</div>
            <div>40 Park Street‬</div>
            <div>Brooklyn, NY 11206, USA</div>
          </ContactWrapper>
        </StyledNav>
      </CSSTransition>
    </StyledWrapper>
  );
}
